<template>
  <v-form ref="APPRENTI" @submit.prevent="sectionSave" lazy-validation class="section">
    <div>
      <section-header v-if="!hideTitle"
                      :ressource="ressource"
                      :isExtended="extended"
                      :dialog="dialog"
                      :picto="picto()"
                      :fiche=fiche
                      :isAbstract=isAbstract
                      @extended=setExtended
                      @closeDialog=closeDialog
                      ref="APPRENTI">
      </section-header>
      <div v-if="extended" id="field-container" :class="dialog ? 'p-0 p-md-3' : 'py-2'">
        <civilite-select v-if="isDisplayed('CIVILITE_ID')"
                         ref="CIVILITE_ID"
                         :theme="theme"
                         :showMissing="showMissing"
                         v-model:value=civiliteId
                         :contact=contact
                         :moduleId=moduleId
                         libelle="'Civilité'"
                         newLabel=''
                         selectText=""
                         @status=statusCiviliteId
                         :requiredAttributs="requiredAttributs"
                         :autoSelect=true
                         :ressource=ressourceCiviliteSelect
                         :filters=civiliteIdFilter
                         :toggleReset=resetCiviliteId
                         :required="isRequired('CIVILITE_ID')"
                         :contextId=apprenti.id
                         :readOnly="readOnly('CIVILITE_ID')"
                         :objType=objType
                         :fiche=fiche
                         :sectionName="sectionName">
        </civilite-select>
        <apprenti-nom v-if="isDisplayed('NOM')"
                      ref="NOM"
                      :theme="theme"
                      :showMissing="showMissing"
                      v-model:value=nom
                      @status=statusNom
                      :over-ride-placeholder=placeholderNom
                      :required="isRequired('NOM')"
                      :contextId=apprenti.id
                      :readOnly="readOnly('NOM')"
                      :objType=objType
                      :fiche=fiche
                      :sectionName="sectionName">
        </apprenti-nom>
        <apprenti-prenom v-if="isDisplayed('PRENOM')"
                         ref="PRENOM"
                         :theme="theme"
                         :showMissing="showMissing"
                         v-model:value=prenom
                         @status=statusPrenom
                         :over-ride-placeholder=placeholderPrenom
                         :required="isRequired('PRENOM')"
                         :contextId=apprenti.id
                         :readOnly="readOnly('PRENOM')"
                         :objType=objType
                         :fiche=fiche
                         :sectionName="sectionName">
        </apprenti-prenom>
        <apprenti-date-debut v-if="isDisplayed('DATE_DEBUT')"
                             ref="DATE_DEBUT"
                             :theme="theme"
                             :showMissing="showMissing"
                             v-model:value=dateDebut
                             @status=statusDateDebut
                             :periods='periodDateDebut'
                             :required="isRequired('DATE_DEBUT')"
                             :contextId=apprenti.id
                             :readOnly="readOnly('DATE_DEBUT')"
                             :objType=objType
                             :fiche=fiche
                             :sectionName="sectionName">
        </apprenti-date-debut>
        <apprenti-date-fin v-if="isDisplayed('DATE_FIN')"
                           ref="DATE_FIN"
                           :theme="theme"
                           :showMissing="showMissing"
                           v-model:value=dateFin
                           @status=statusDateFin
                           :periods='periodDateFin'
                           :required="isRequired('DATE_FIN')"
                           :contextId=apprenti.id
                           :readOnly="readOnly('DATE_FIN')"
                           :objType=objType
                           :fiche=fiche
                           :sectionName="sectionName">
        </apprenti-date-fin>
        <apprenti-date-rupture v-if="isDisplayed('DATE_RUPTURE')"
                               ref="DATE_RUPTURE"
                               :theme="theme"
                               :showMissing="showMissing"
                               v-model:value=dateRupture
                               @status=statusDateRupture
                               :periods='periodDateRupture'
                               :required="isRequired('DATE_RUPTURE')"
                               :contextId=apprenti.id
                               :readOnly="readOnly('DATE_RUPTURE')"
                               :objType=objType
                               :fiche=fiche
                               :sectionName="sectionName">
        </apprenti-date-rupture>
      </div>
    </div>
    <div v-if="extended" class="d-flex flex-row-reverse">
      <loader-button v-if="saveButton"
                     class="ms-2"
                     type="submit"
                     :loading="saveLoading"
                     :label="saveLabel"
                     :disabled="!updatedAndValid">
      </loader-button>
      <div v-for="(button, index) in extraButtons" :key="index">
        <loader-button class="ms-2"
                       :outlined="button.outlined"
                       @callback="button.callback()"
                       :type="button.type"
                       :label="button.label"
                       :loading="button.loading"
                       :disabled="button.disabled">
        </loader-button>
      </div>
    </div>
    <div class="d-flex flex-row-reverse" v-if="saveFailed && saveButton && extended">
      <p class="text-12 text-bold error-text">{{errorTxt}}</p>
    </div>
  </v-form>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  import CrudApprenti from '@/generated/cruds/CrudApprenti';
  // eslint-disable-next-line
  import { GenControl, GenSection, RessourceGenControl, RessourceGenSection } from '@/generated/ressources/generatedRessources';
  import { FwkButtons, RessourceFwkButton, RessourceFwkSection, FwkSections } from '@/ressources/globalRessources';
  import { Sections } from '@/generated/classes/AppSchema';
  import Button, { ButtonCallback } from '@/framework/base/classes/Button';
  import { ColumnFilter, ListChoix } from '@/extend/classes/ListData';
  import { ObjTypes } from '@/generated/classes/ObjTypes';

  export default defineComponent({
    extends:  CrudApprenti,
    components: {},
    props: {
      hideTitle: {
        type: Boolean,
      },
      isAbstract: {
        type: Boolean,
      },
    },

    data() {
      return {
        sectionName: Sections.APPRENTI,
        displayMap: new Map<string, Boolean>,
        readOnlyMap: new Map<string, Boolean>,
        requiredMap: new Map<string, Boolean>,
        placeholderNom: null,
        placeholderPrenom: null,
      }
    },

    computed: {
      saveLabel () { 
        return RessourceFwkButton(FwkButtons.ENREGISTRER).label;
      },

      errorTxt () { 
        return RessourceFwkSection(FwkSections.ERROR_TEXT).label;
      },

      ressourceCiviliteSelect () { return RessourceGenControl(GenControl.APPRENTI_CIVILITE_SELECT); },

      ressource () { return RessourceGenSection(GenSection.APPRENTI); },

      fields (): string[] {
        return ['CIVILITE_ID', 'NOM', 'PRENOM', 'DATE_DEBUT', 'DATE_FIN', 'DATE_RUPTURE'];
      },

      extraButtons (): Button[] {
        return [];
      },

      civiliteIdFilter (): ColumnFilter[] {
        const res: ColumnFilter[] = [];
        return res;
      },
    },
    methods: {
      getRequiredAttributs(): string[] {
        const res: string[] = [];
        if (this.requiredAttributs) {
          this.requiredAttributs.forEach((attrib) => {
            const value = attrib.split(".");
            if (value[0] == this.sectionName) {
              res.push(value[1])
            }
          })
        }
        return res;
      },

      requiredMissing(requiredAttributs: string[], value: string): boolean {
        return requiredAttributs.length > 0 && requiredAttributs.includes(value);;
      },

      readOnly (value: string): boolean {
        let res = false;
        if (!this.editApprenti.canUpdate) {
          return true;
        }
        if (this.readOnlyMap.size == 0) {
          this.readOnlyMap.set("CIVILITE_ID", false);
          this.readOnlyMap.set("NOM", false);
          this.readOnlyMap.set("PRENOM", false);
          this.readOnlyMap.set("DATE_DEBUT", false);
          this.readOnlyMap.set("DATE_FIN", false);
          this.readOnlyMap.set("DATE_RUPTURE", false);
        }
        const readOnly = this.readOnlyMap.get(value);
        if (readOnly != undefined) {
          res = readOnly;
        }
        return res;
      },

      isDisplayed (value: string): boolean {
        let res = true;
        if (this.displayMap.size == 0) {
        const requiredAttributs = this.getRequiredAttributs();
          this.displayMap.set("CIVILITE_ID", true);
          this.displayMap.set("NOM", true);
          this.displayMap.set("PRENOM", true);
          this.displayMap.set("DATE_DEBUT", true);
          this.displayMap.set("DATE_FIN", true);
          this.displayMap.set("DATE_RUPTURE", this.requiredMissing(requiredAttributs, "DATE_RUPTURE") || !!this.fiche);
        }
        const display = this.displayMap.get(value);
        if (display != undefined) {
          res = display;
        }
        return res;
      },

      isRequired (value: string): boolean {
        let res = true;
        const requiredAttributs = this.getRequiredAttributs();
        if (this.requiredMap.size == 0) {
          this.requiredMap.set("CIVILITE_ID", true);
          this.requiredMap.set("NOM", true);
          this.requiredMap.set("PRENOM", true);
          this.requiredMap.set("DATE_DEBUT", true);
          this.requiredMap.set("DATE_FIN", true);
          this.requiredMap.set("DATE_RUPTURE", this.requiredMissing(requiredAttributs, "DATE_RUPTURE") || !this.fiche);
        }
        const required = this.requiredMap.get(value);
        if (required != undefined) {
          res = required;
        }
        return res;
      },

      picto (): string {
        return 'SectionPictoApprenti' 
      },

      sectionSave (): void {
        return this.save();
      }
    }
  })
</script>
